import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

// Types
export type SiteVisitType = 'progress' | 'safety' | 'punch_list' | 'site_visit_info' | 'site_review' | 'other';
export type SiteVisitStatus = 'draft' | 'in_progress' | 'completed' | 'submitted';
// Free-form string — valid values are configured per-tenant in site_visit_defaults settings
export type ChecklistItemStatus = string;
export type WeatherCondition =
  | 'clear' | 'partly_cloudy' | 'cloudy' | 'rain' | 'heavy_rain'
  | 'snow' | 'fog' | 'thunderstorm' | 'windy';

export interface WeatherData {
  temperature_c: number | null;
  conditions: WeatherCondition;
  humidity_pct: number | null;
  wind_kph: number | null;
  source: string;
}

export interface SiteVisit {
  id: string;
  tenant_id: string;
  project_id: string;
  visit_date: string;
  visited_by: string | null;
  recipient: string | null;
  visit_type: SiteVisitType;
  status: SiteVisitStatus;
  title: string;
  notes: string | null;
  general_conditions: string | null;
  location_description: string | null;
  weather_notes: string | null;
  weather_data: WeatherData | null;
  started_at: string | null;
  created_by: string;
  photo_count: number;
  checklist_item_count: number;
  created_at: string;
  updated_at: string;
  submission_id?: string | null;
  deliverable_id?: string | null;
  deliverable_title?: string | null;
  deliverable_status?: string | null;
}

export interface VisitPhoto {
  id: string;
  site_visit_id: string;
  file_name: string;
  file_size: number;
  content_type: string;
  caption: string | null;
  tags: string[] | null;
  sort_order: number;
  uploaded_by: string;
  created_at: string;
}

export type ChecklistItemType = 'info' | 'action';

export interface ChecklistItem {
  id: string;
  site_visit_id: string;
  description: string;
  status: ChecklistItemStatus;
  notes: string | null;
  photo_ids: string[];
  sort_order: number;
  item_type: ChecklistItemType;
  assigned_group: string | null;
  response: string | null;
  response_at: string | null;
  response_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface SiteVisitCreate {
  title: string;
  visit_date: string;
  visited_by?: string;
  visit_type?: SiteVisitType;
  notes?: string;
  location_description?: string;
  weather_notes?: string;
  weather_data?: WeatherData;
  recipient?: string;
  submission_id?: string | null;
  deliverable_id?: string | null;
}

export interface SiteVisitUpdate {
  title?: string;
  visited_by?: string;
  visit_type?: SiteVisitType;
  status?: SiteVisitStatus;
  notes?: string;
  location_description?: string;
  weather_notes?: string;
  weather_data?: WeatherData;
  recipient?: string;
  submission_id?: string | null;
  deliverable_id?: string | null;
}

// Query Hooks
export function useProjectWeather(projectId: string | null | undefined) {
  return useQuery({
    queryKey: ['weather', projectId],
    queryFn: async () => {
      const response = await api.siteVisits.getWeather(projectId!);
      return response.weather as WeatherData;
    },
    enabled: !!projectId,
    retry: false,
    staleTime: 30 * 60 * 1000, // 30 minutes — matches server cache
  });
}

export function useSiteVisits(projectId: string | undefined) {
  return useQuery({
    queryKey: ['site-visits', projectId],
    queryFn: async () => {
      const response = await api.siteVisits.list(projectId!);
      return response.site_visits as SiteVisit[];
    },
    enabled: !!projectId,
  });
}

export function useSiteVisit(visitId: string | null) {
  return useQuery({
    queryKey: ['site-visit', visitId],
    queryFn: async () => {
      const response = await api.siteVisits.get(visitId!);
      return {
        visit: response.site_visit as SiteVisit,
        photos: response.photos as VisitPhoto[],
        checklist_items: response.checklist_items as ChecklistItem[],
      };
    },
    enabled: !!visitId,
  });
}

// Mutation Hooks
export function useCreateSiteVisit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, visit }: { projectId: string; visit: SiteVisitCreate }) =>
      api.siteVisits.create(projectId, visit),
    onSuccess: (response) => {
      const newVisit = response?.site_visit || response;
      // Target only this project's cache — prevents cross-project cache contamination (R8d)
      queryClient.setQueryData<any>(
        ['site-visits', newVisit.project_id],
        (old: any) => {
          if (!Array.isArray(old)) return old;
          return [...old, newVisit];
        }
      );
      // Don't invalidate ['site-visits'] — we just set that cache.
      toast.success('Site visit created.');
      // Invalidate deliverable cache so linked_site_visits refreshes
      if (newVisit?.deliverable_id) {
        queryClient.invalidateQueries({ queryKey: ['deliverable', newVisit.deliverable_id] });
      }
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create site visit');
    },
  });
}

export function useUpdateSiteVisit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SiteVisitUpdate }) =>
      api.siteVisits.update(id, data),
    onSuccess: (response, variables) => {
      const updatedVisit = response?.site_visit || response;
      // Update the list cache
      queryClient.setQueriesData<any>(
        { predicate: (query) => query.queryKey[0] === 'site-visits' },
        (old) => {
          if (!Array.isArray(old)) return old;
          return old.map((v: any) =>
            v.id === variables.id ? { ...v, ...updatedVisit } : v
          );
        }
      );
      // Update the single-visit cache
      queryClient.setQueriesData<any>(
        { predicate: (query) => query.queryKey[0] === 'site-visit' },
        (old) => {
          if (!old?.visit || old.visit.id !== variables.id) return old;
          return { ...old, visit: { ...old.visit, ...updatedVisit } };
        }
      );
      queryClient.invalidateQueries({ queryKey: ['site-visits'] });
      queryClient.invalidateQueries({ queryKey: ['site-visit'] });
      // Fire toast for status transitions that have locked copy (R8a)
      if (variables.data.status === 'submitted') {
        toast.success('Submitted.');
      }
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update site visit');
    },
  });
}

export function useDeleteSiteVisit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (visitId: string) => api.siteVisits.delete(visitId),
    onSuccess: (_, visitId) => {
      // Remove from list cache immediately
      queryClient.setQueriesData<any>(
        { predicate: (query) => query.queryKey[0] === 'site-visits' },
        (old) => {
          if (!Array.isArray(old)) return old;
          return old.filter((v: any) => v.id !== visitId);
        }
      );
      // Don't invalidate ['site-visits'] — we just set that cache.
      toast.success('Site visit deleted');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete site visit');
    },
  });
}

export function useCompleteSiteVisit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (visitId: string) => api.siteVisits.complete(visitId),
    onSuccess: (response, visitId) => {
      const updatedVisit = response?.site_visit || response;
      // Update the list cache
      queryClient.setQueriesData<any>(
        { predicate: (query) => query.queryKey[0] === 'site-visits' },
        (old) => {
          if (!Array.isArray(old)) return old;
          return old.map((v: any) =>
            v.id === visitId ? { ...v, ...updatedVisit } : v
          );
        }
      );
      // Update the single-visit cache
      queryClient.setQueriesData<any>(
        { predicate: (query) => query.queryKey[0] === 'site-visit' },
        (old) => {
          if (!old?.visit || old.visit.id !== visitId) return old;
          return { ...old, visit: { ...old.visit, ...updatedVisit } };
        }
      );
      queryClient.invalidateQueries({ queryKey: ['site-visits'] });
      queryClient.invalidateQueries({ queryKey: ['site-visit'] });
      toast.success('Marked complete.');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to complete site visit');
    },
  });
}

// Photo Mutations
export function useUploadVisitPhoto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ visitId, file }: { visitId: string; file: File }) =>
      api.siteVisits.photos.upload(visitId, file),
    onSuccess: (_, { visitId }) => {
      queryClient.invalidateQueries({ queryKey: ['site-visit'] });
      queryClient.invalidateQueries({ queryKey: ['site-visits'] });
      queryClient.invalidateQueries({ queryKey: ['visit-photos', visitId] });
      toast.success('Photo uploaded');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to upload photo');
    },
  });
}

export function useDeleteVisitPhoto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ visitId, photoId }: { visitId: string; photoId: string }) =>
      api.siteVisits.photos.delete(visitId, photoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-visit'] });
      queryClient.invalidateQueries({ queryKey: ['site-visits'] });
      toast.success('Photo deleted');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete photo');
    },
  });
}

export function useUpdateVisitPhoto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      visitId,
      photoId,
      data,
    }: {
      visitId: string;
      photoId: string;
      data: { caption?: string; tags?: string[]; sort_order?: number };
    }) => api.siteVisits.photos.update(visitId, photoId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-visit'] });
      toast.success('Photo updated');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update photo');
    },
  });
}

// Checklist Mutations
export function useCreateChecklistItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      visitId,
      item,
    }: {
      visitId: string;
      item: {
        description: string;
        status?: ChecklistItemStatus;
        notes?: string;
        item_type?: ChecklistItemType;
        assigned_group?: string;
        response?: string;
        response_at?: string;
        response_by?: string;
      };
    }) => api.siteVisits.checklist.create(visitId, item),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-visit'] });
      queryClient.invalidateQueries({ queryKey: ['site-visits'] });
      toast.success('Checklist item added');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to add checklist item');
    },
  });
}

export function useUpdateChecklistItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      visitId,
      itemId,
      data,
    }: {
      visitId: string;
      itemId: string;
      data: {
        status?: ChecklistItemStatus;
        notes?: string;
        description?: string;
        item_type?: ChecklistItemType;
        assigned_group?: string;
        response?: string;
        response_at?: string;
        response_by?: string;
      };
    }) => api.siteVisits.checklist.update(visitId, itemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-visit'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update checklist item');
    },
  });
}

export function useDeleteChecklistItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ visitId, itemId }: { visitId: string; itemId: string }) =>
      api.siteVisits.checklist.delete(visitId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-visit'] });
      queryClient.invalidateQueries({ queryKey: ['site-visits'] });
      toast.success('Checklist item deleted');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete checklist item');
    },
  });
}

export function useLinkChecklistPhotos() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      visitId,
      itemId,
      photoIds,
    }: {
      visitId: string;
      itemId: string;
      photoIds: string[];
    }) => api.siteVisits.checklist.linkPhotos(visitId, itemId, photoIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-visit'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to link photo');
    },
  });
}

// Linked site visits for submission/deliverable
export function useSiteVisitsForSubmission(submissionId: string | null | undefined) {
  return useQuery({
    queryKey: ['site-visits-for-submission', submissionId],
    queryFn: async () => {
      const response = await api.siteVisits.forSubmission(submissionId!);
      return response.site_visits as SiteVisit[];
    },
    enabled: !!submissionId,
  });
}

export function useSiteVisitsForDeliverable(deliverableId: string | null | undefined) {
  return useQuery({
    queryKey: ['site-visits-for-deliverable', deliverableId],
    queryFn: async () => {
      const response = await api.siteVisits.forDeliverable(deliverableId!);
      return response.site_visits as SiteVisit[];
    },
    enabled: !!deliverableId,
  });
}

export function useUnlinkChecklistPhoto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      visitId,
      itemId,
      photoId,
    }: {
      visitId: string;
      itemId: string;
      photoId: string;
    }) => api.siteVisits.checklist.unlinkPhoto(visitId, itemId, photoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-visit'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to unlink photo');
    },
  });
}

// R2: update general_conditions and/or weather_notes (strict allowlist endpoint)
export function useUpdateSiteVisitConditions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { general_conditions?: string; weather_notes?: string } }) =>
      api.siteVisits.updateConditions(id, data),
    onSuccess: (response, variables) => {
      const updatedVisit = response?.site_visit || response;
      queryClient.setQueriesData<any>(
        { predicate: (query) => query.queryKey[0] === 'site-visit' },
        (old) => {
          if (!old?.visit || old.visit.id !== variables.id) return old;
          return { ...old, visit: { ...old.visit, ...updatedVisit } };
        }
      );
      queryClient.setQueriesData<any>(
        { predicate: (query) => query.queryKey[0] === 'site-visits' },
        (old) => {
          if (!Array.isArray(old)) return old;
          return old.map((v: any) => v.id === variables.id ? { ...v, ...updatedVisit } : v);
        }
      );
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to save');
    },
  });
}

// R2: reopen a submitted visit for correction
export function useReopenSiteVisit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.siteVisits.reopen(id, reason),
    onSuccess: (response, variables) => {
      const updatedVisit = response?.site_visit || response;
      queryClient.setQueriesData<any>(
        { predicate: (query) => query.queryKey[0] === 'site-visit' },
        (old) => {
          if (!old?.visit || old.visit.id !== variables.id) return old;
          return { ...old, visit: { ...old.visit, ...updatedVisit } };
        }
      );
      queryClient.setQueriesData<any>(
        { predicate: (query) => query.queryKey[0] === 'site-visits' },
        (old) => {
          if (!Array.isArray(old)) return old;
          return old.map((v: any) => v.id === variables.id ? { ...v, ...updatedVisit } : v);
        }
      );
      toast.success('Reopened for correction.');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to reopen');
    },
  });
}

// Per-QueryClient singleton: registers one cache listener that revokes blob URLs on GC.
// Using a WeakSet so the subscription is tied to the QueryClient instance's lifetime.
const blobRevokerRegistered = new WeakSet<object>();

function ensureBlobRevoker(queryClient: ReturnType<typeof useQueryClient>) {
  const cache = queryClient.getQueryCache();
  if (blobRevokerRegistered.has(cache)) return;
  blobRevokerRegistered.add(cache);
  cache.subscribe((event) => {
    if (event.type === 'removed' && event.query.queryKey[0] === 'visit-photo-blob') {
      const blobUrl = event.query.state.data as string | undefined;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    }
  });
}

// ── R4: Email recipients + send + submit-without-sending ─────────────────────

export interface SiteVisitEmailRecipient {
  email: string;
  name: string;
  source: 'portal_member' | 'scope_contact';
}

export function useSiteVisitEmailRecipients(visitId: string | null) {
  return useQuery({
    queryKey: ['site-visit-email-recipients', visitId],
    queryFn: () => api.siteVisits.emailRecipients(visitId!),
    enabled: Boolean(visitId),
    staleTime: 5 * 60 * 1000,
  });
}

export function useSendSiteVisitEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      visitId,
      toRecipients,
      ccRecipients,
      customNote,
    }: {
      visitId: string;
      toRecipients: string[];
      ccRecipients?: string[];
      customNote?: string;
    }) =>
      api.siteVisits.sendEmail(visitId, {
        to_recipients: toRecipients,
        cc_recipients: ccRecipients,
        custom_note: customNote,
      }),
    onSuccess: (_data, { visitId }) => {
      queryClient.invalidateQueries({ queryKey: ['site-visit', visitId] });
      queryClient.invalidateQueries({ queryKey: ['site-visits'] });
      toast.success('Report sent. Visit submitted.');
    },
    onError: (error: any) => {
      const msg = error?.message || 'Failed to send email';
      if (error?.status === 429) {
        toast.error('Rate limit reached: too many sends today.');
      } else {
        toast.error(msg);
      }
    },
  });
}

export function useSubmitSiteVisitWithoutSending() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (visitId: string) => api.siteVisits.submitWithoutSending(visitId),
    onSuccess: (_data, visitId) => {
      queryClient.invalidateQueries({ queryKey: ['site-visit', visitId] });
      queryClient.invalidateQueries({ queryKey: ['site-visits'] });
      toast.success('Visit submitted.');
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to submit visit');
    },
  });
}

// Authenticated photo URL hook — fetches photo with Bearer token, returns blob URL
export function useVisitPhotoUrl(visitId: string, photoId: string) {
  const queryClient = useQueryClient();

  // Register the blob-revoke listener once per QueryClient — deliberately no cleanup return,
  // so the subscription outlives any individual component. This prevents the
  // editable→read-only flip after Mark Complete from revoking a blob URL still needed by
  // the newly-mounted read-only branch (net::ERR_FILE_NOT_FOUND fix, R5).
  useEffect(() => {
    ensureBlobRevoker(queryClient);
  }, [queryClient]);

  const query = useQuery({
    queryKey: ['visit-photo-blob', visitId, photoId],
    queryFn: () => api.siteVisits.photos.fetchBlob(visitId, photoId),
    staleTime: 30 * 60 * 1000, // 30 min — photos don't change
    gcTime: 60 * 60 * 1000, // keep in cache 1 hour
    retry: 1,
  });

  return query;
}
